import { useWidgetConfig } from "@/context/WidgetConfigContext";
import { Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

interface VoiceIdleScreenProps {
  onStartCall: () => void;
  isConnecting?: boolean;
}

/**
 * Pre-call screen with animated start button
 */
export function VoiceIdleScreen({
  onStartCall,
  isConnecting,
}: VoiceIdleScreenProps) {
  const { config } = useWidgetConfig();

  return (
    <div className="flex flex-col items-center justify-center gap-6 p-8 text-center">
      {/* Description */}
      <p className="text-sm text-muted-foreground max-w-xs">
        {config.voice_chat_description || "Tap to start a voice conversation"}
      </p>

      {/* Start call button with pulse animation */}
      <div className="relative">
        {/* Pulse rings (only when idle, not connecting) */}
        {!isConnecting && (
          <>
            <div
              className="absolute inset-0 rounded-full animate-ping opacity-20"
              style={{ backgroundColor: config.primary_color }}
            />
            <div
              className="absolute inset-[-8px] rounded-full animate-pulse opacity-10"
              style={{ backgroundColor: config.primary_color }}
            />
          </>
        )}

        <Button
          onClick={onStartCall}
          disabled={isConnecting}
          size="lg"
          className="relative w-20 h-20 rounded-full shadow-lg transition-all duration-300 hover:scale-105 active:scale-95"
          style={{
            backgroundColor: config.primary_color,
            color: config.header_text_color,
          }}
        >
          {isConnecting ? (
            <div className="flex items-center gap-1">
              <span
                className="w-2 h-2 rounded-full bg-current animate-bounce"
                style={{ animationDelay: "0ms" }}
              />
              <span
                className="w-2 h-2 rounded-full bg-current animate-bounce"
                style={{ animationDelay: "150ms" }}
              />
              <span
                className="w-2 h-2 rounded-full bg-current animate-bounce"
                style={{ animationDelay: "300ms" }}
              />
            </div>
          ) : (
            <Phone className="w-8 h-8" />
          )}
        </Button>
      </div>

      {/* Status text */}
      <p className="text-xs text-muted-foreground">
        {isConnecting ? "Connecting..." : "Tap to start call"}
      </p>
    </div>
  );
}
